import React, { useState, useEffect, useCallback, useRef } from 'react';
import FingerprintIcon from './shared/FingerprintIcon';

function base64urlToBuffer(base64url) {
  var padding = '='.repeat((4 - base64url.length % 4) % 4);
  var base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  var binary = atob(base64);
  var bytes = new Uint8Array(binary.length);
  for (var i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  var bytes = new Uint8Array(buffer);
  var binary = '';
  for (var i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function getCsrfToken() {
  var meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  var d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function detectBrowser() {
  var ua = navigator.userAgent;
  if (ua.indexOf('Edg/') > -1) return 'Edge ' + (ua.match(/Edg\/(\d+)/) || [])[1];
  if (ua.indexOf('OPR/') > -1) return 'Opera ' + (ua.match(/OPR\/(\d+)/) || [])[1];
  if (ua.indexOf('Brave') > -1) return 'Brave';
  if (ua.indexOf('Chrome/') > -1) return 'Chrome ' + (ua.match(/Chrome\/(\d+)/) || [])[1];
  if (ua.indexOf('Safari/') > -1 && ua.indexOf('Chrome') === -1) return 'Safari ' + (ua.match(/Version\/(\d+)/) || [])[1];
  if (ua.indexOf('Firefox/') > -1) return 'Firefox ' + (ua.match(/Firefox\/(\d+)/) || [])[1];
  return null;
}

function extractBrowserInfo(nickname) {
  if (!nickname) return null;
  var browsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera', 'Brave'];
  for (var i = 0; i < browsers.length; i++) {
    var match = nickname.match(new RegExp(browsers[i] + '\\s*\\d*', 'i'));
    if (match) return match[0];
  }
  return null;
}

function isDesktopDevice(nickname) {
  var lower = (nickname || '').toLowerCase();
  if (/iphone|ipad|android|mobile|phone/i.test(lower)) return false;
  return true;
}

function DesktopIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8M12 17v4"/>
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5" y="2" width="14" height="20" rx="2"/>
      <path d="M12 18h.01"/>
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="8" cy="15" r="3"/>
      <path d="M8 12V2l4 2-4 2"/>
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
    </svg>
  );
}

function PasskeyManager(props) {
  var { passkeysUrl, registrationOptionsUrl, deleteUrlTemplate, translations } = props;
  var [passkeys, setPasskeys] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [nickname, setNickname] = useState('');
  var [registering, setRegistering] = useState(false);
  var [modalError, setModalError] = useState(null);
  var [deleteTarget, setDeleteTarget] = useState(null);
  var [deleting, setDeleting] = useState(false);
  var modalRef = useRef(null);
  var deleteModalRef = useRef(null);

  var fetchPasskeys = useCallback(function () {
    setLoading(true);
    fetch(passkeysUrl, {
      headers: { 'Accept': 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to fetch passkeys');
        return res.json();
      })
      .then(function (data) {
        setPasskeys(data.data || []);
      })
      .catch(function () {
        setPasskeys([]);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [passkeysUrl]);

  useEffect(function () {
    fetchPasskeys();
  }, [fetchPasskeys]);

  useEffect(function () {
    if (showModal && modalRef.current && window.$) {
      var $modal = window.$(modalRef.current);
      $modal.modal({ backdrop: 'static', keyboard: false });
      $modal.modal('show');

      var handleHidden = function () {
        setShowModal(false);
        setNickname('');
        setModalError(null);
        setRegistering(false);
      };
      $modal.on('hidden.bs.modal', handleHidden);

      return function () {
        $modal.off('hidden.bs.modal', handleHidden);
      };
    }
  }, [showModal]);

  var handleOpenModal = useCallback(function () {
    setShowModal(true);
    setNickname('');
    setModalError(null);
  }, []);

  var handleCloseModal = useCallback(function () {
    if (modalRef.current && window.$) {
      window.$(modalRef.current).modal('hide');
    }
  }, []);

  var handleRegister = useCallback(function () {
    if (registering) return;
    if (!nickname.trim()) return;

    setModalError(null);
    setRegistering(true);

    var csrfToken = getCsrfToken();

    fetch(registrationOptionsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
      },
      body: JSON.stringify({}),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to get registration options');
        return res.json();
      })
      .then(function (data) {
        var options = data.data;

        var publicKeyOptions = {
          challenge: base64urlToBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: base64urlToBuffer(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          attestation: options.attestation || 'none',
          authenticatorSelection: options.authenticatorSelection,
        };

        if (options.excludeCredentials && options.excludeCredentials.length > 0) {
          publicKeyOptions.excludeCredentials = options.excludeCredentials.map(function (cred) {
            return {
              id: base64urlToBuffer(cred.id),
              type: cred.type,
              transports: cred.transports,
            };
          });
        }

        return navigator.credentials.create({ publicKey: publicKeyOptions }).then(function (credential) {
          return { credential: credential };
        });
      })
      .then(function (result) {
        var credential = result.credential;

        var serialized = {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          response: {
            attestationObject: bufferToBase64url(credential.response.attestationObject),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          },
          type: credential.type,
        };

        return fetch(passkeysUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            credential: serialized,
            nickname: nickname.trim(),
          }),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Registration failed');
        return res.json();
      })
      .then(function () {
        handleCloseModal();
        fetchPasskeys();
        if (window.notice) window.notice(translations.success_registered, 'success');
      })
      .catch(function (err) {
        var msg = translations.error_registration;
        if (err && err.message) msg = msg + ': ' + err.message;
        setModalError(msg);
        setRegistering(false);
      });
  }, [registering, nickname, registrationOptionsUrl, passkeysUrl, translations, handleCloseModal, fetchPasskeys]);

  var handleDeleteClick = useCallback(function (passkey) {
    setDeleteTarget(passkey);
  }, []);

  useEffect(function () {
    if (deleteTarget && deleteModalRef.current && window.$) {
      var $modal = window.$(deleteModalRef.current);
      $modal.modal({ backdrop: 'static', keyboard: true });
      $modal.modal('show');

      var handleHidden = function () {
        setDeleteTarget(null);
        setDeleting(false);
      };
      $modal.on('hidden.bs.modal', handleHidden);

      return function () {
        $modal.off('hidden.bs.modal', handleHidden);
      };
    }
  }, [deleteTarget]);

  var handleDeleteConfirm = useCallback(function () {
    if (!deleteTarget || deleting) return;
    setDeleting(true);

    var csrfToken = getCsrfToken();
    var deleteUrl = deleteUrlTemplate.replace(':id', deleteTarget.id);

    fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
      },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Delete failed');
        return res.json();
      })
      .then(function () {
        if (deleteModalRef.current && window.$) {
          window.$(deleteModalRef.current).modal('hide');
        }
        fetchPasskeys();
        if (window.notice) window.notice(translations.success_deleted, 'success');
      })
      .catch(function () {
        if (deleteModalRef.current && window.$) {
          window.$(deleteModalRef.current).modal('hide');
        }
        if (window.notice) window.notice(translations.error_delete || 'Failed to delete passkey', 'danger');
      });
  }, [deleteTarget, deleting, deleteUrlTemplate, translations, fetchPasskeys]);

  var handleDeleteCancel = useCallback(function () {
    if (deleteModalRef.current && window.$) {
      window.$(deleteModalRef.current).modal('hide');
    }
  }, []);

  var countText = function () {
    var count = passkeys.length;
    if (count === 0) return translations.count_zero;
    if (count === 1) return translations.count_one.replace('%{count}', count);
    return translations.count_other.replace('%{count}', count);
  };

  return (
    <div>
      <div className="passkey-info__card">
        <div className="passkey-info__card-icon">
          <FingerprintIcon size={40} className="text-muted" />
        </div>
        <div className="passkey-info__card-text">
          <h6>{translations.info_title}</h6>
          <p>{translations.info_description}</p>
        </div>
      </div>

      <div className="passkey-list__card">
        <div className="passkey-list__header">
          <div>
            <h5>{translations.title}</h5>
            <span>{countText()}</span>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenModal}
          >
            + {translations.add_passkey}
          </button>
        </div>

        {loading && (
          <div className="passkey-list__empty">
            <div className="spinner-border spinner-border-sm text-muted" role="status" />
          </div>
        )}

        {!loading && passkeys.length === 0 && (
          <div className="passkey-list__empty">
            {translations.count_zero}
          </div>
        )}

        {!loading && passkeys.map(function (passkey) {
          var browserInfo = extractBrowserInfo(passkey.nickname) || detectBrowser();
          var desktop = isDesktopDevice(passkey.nickname);
          var createdDate = formatDate(passkey.created_at);
          var lastUsedDate = formatDate(passkey.last_used_at);

          return (
            <div key={passkey.id} className="passkey-item">
              <div className="passkey-item__icon">
                {desktop ? <DesktopIcon /> : <MobileIcon />}
              </div>
              <div className="passkey-item__info">
                <div className="passkey-item__name">
                  <strong>{passkey.nickname}</strong>
                  {browserInfo && (
                    <span className="passkey-item__badge">
                      <KeyIcon />
                      {browserInfo}
                    </span>
                  )}
                </div>
                <div className="passkey-item__meta">
                  {translations.created.replace('%{date}', createdDate)}
                  {' \u00B7 '}
                  {lastUsedDate
                    ? translations.last_used.replace('%{date}', lastUsedDate)
                    : translations.never_used}
                </div>
              </div>
              <button
                type="button"
                className="passkey-item__delete"
                onClick={function () { handleDeleteClick(passkey); }}
              >
                <TrashIcon />
              </button>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div ref={modalRef} className="modal fade passkey-modal" tabIndex="-1" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{translations.register_title}</h5>
              </div>
              <div className="modal-body">
                <p className="passkey-modal__desc">{translations.register_description}</p>
                {modalError && (
                  <div className="alert alert-danger mb-3">{modalError}</div>
                )}
                <label className="passkey-modal__label">{translations.nickname_label}</label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={translations.nickname_placeholder}
                    value={nickname}
                    onChange={function (e) { setNickname(e.target.value); }}
                    disabled={registering}
                  />
                  <i className="form-group__bar" />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link text-muted"
                  onClick={handleCloseModal}
                  disabled={registering}
                >
                  {translations.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRegister}
                  disabled={registering || !nickname.trim()}
                >
                  {registering ? (
                    <span>
                      <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                      {translations.register_button}
                    </span>
                  ) : (
                    <span>
                      <FingerprintIcon size={16} />
                      {' '}{translations.register_button}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div ref={deleteModalRef} className="modal fade passkey-modal" tabIndex="-1" aria-hidden="true">
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{translations.register_title.replace('Register New', 'Remove')}</h5>
              </div>
              <div className="modal-body">
                <p className="passkey-modal__desc">{translations.delete_confirm}</p>
                {deleteTarget.nickname && (
                  <div className="passkey-item__name mb-0">
                    <strong>{deleteTarget.nickname}</strong>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link text-muted"
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                >
                  {translations.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? (
                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                  ) : (
                    <TrashIcon />
                  )}
                  {' Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PasskeyManager;
